import {latLngBounds} from 'leaflet';
import {useCallback, useMemo} from 'react';
import {MapContainer as LeafletMap, TileLayer} from 'react-leaflet';

import '../../assets/leaflet-overrides.css';
import {MAP_DEFAULTS} from '../../utils/constants';

import {ClusterPin} from './ClusterPin';
import {MapController} from './MapController';
import {MarkerPin} from './MarkerPin';

import type {GeoFeatureProperties} from '../../functions/createGeoFeatures';
import type {LatLngBounds, Map as LeafletMapInterface} from 'leaflet';
import type {PointFeature, ClusterFeature} from 'supercluster';

interface Props<P> {
  markers: PointFeature<P>[];
  active?: {id: string; markup: string};
  forcedMarker?: PointFeature<P>;
  markerPopup?: (
    m: PointFeature<P>,
    map: LeafletMapInterface
  ) => React.ReactNode;
  clusterPopup?: (
    leaves: PointFeature<P>[],
    map: LeafletMapInterface
  ) => React.ReactNode;
  mapChildren?: React.ReactNode;
  children?: (
    map: LeafletMapInterface,
    initialBounds: LatLngBounds,
    markers: PointFeature<P>[],
    clusters: ClusterFeature<P>[]
  ) => React.ReactNode;
  options?: Partial<typeof MAP_DEFAULTS>;
}
export function Map<P extends GeoFeatureProperties>(
  props: Props<P>
): React.JSX.Element {
  const initialBounds: LatLngBounds = useMemo(
    () =>
      props.markers.length
        ? latLngBounds(
            props.markers.map((m) => [
              m.geometry.coordinates[1],
              m.geometry.coordinates[0],
            ])
          )
        : // view whole map as fallback
          latLngBounds([
            [-180, 90],
            [-180, -90],
            [180, 90],
            [180, -90],
          ]),
    [props.markers]
  );

  const mapRef = useCallback(
    (map: LeafletMapInterface) => {
      map?.fitBounds(initialBounds);
    },
    [initialBounds]
  );

  const markersWithActive = useMemo(() => {
    const list = [...props.markers];
    props.forcedMarker && list.push(props.forcedMarker);

    return list.map((marker) => {
      return props.active?.id === marker.properties.id
        ? {
            ...marker,
            properties: {...marker.properties, markup: props.active.markup},
          }
        : marker;
    });
  }, [props.markers, props.forcedMarker, props.active]);

  const minZoom = props.options?.minZoom ?? MAP_DEFAULTS.minZoom;
  const maxZoom = props.options?.maxZoom ?? MAP_DEFAULTS.maxZoom;
  const maxNativeZoom =
    props.options?.maxNativeZoom ?? MAP_DEFAULTS.maxNativeZoom;
  const shouldPadPopup =
    props.options?.shouldPadPopup ?? MAP_DEFAULTS.shouldPadPopup;

  return (
    <div className="relative flex h-[inherit] w-full grow overflow-hidden bg-black">
      <LeafletMap
        ref={mapRef}
        center={initialBounds.getCenter()}
        zoom={minZoom}
        minZoom={minZoom}
        zoomControl={false}
        doubleClickZoom={false}
        attributionControl={false}
        className="h-[inherit] w-full"
        preferCanvas
      >
        <TileLayer
          url="https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}.jpg90?access_token=pk.eyJ1IjoiaW5kdXN0cmlhbHNreXdvcmtzIiwiYSI6ImNrNDA2eGZxcTAwaTgzZXBjcWZjMTBsMHYifQ.ipPXxHnYqvKvtpWlq9yOkg"
          maxZoom={maxZoom}
          maxNativeZoom={maxNativeZoom}
          noWrap
        />

        {props.mapChildren}

        <MapController<P>
          markers={markersWithActive}
          minZoom={minZoom}
          maxZoom={maxZoom}
        >
          {(map, markers, clusters, sc) => (
            <>
              {markers.map((marker) => (
                <MarkerPin
                  key={marker.properties.id}
                  marker={marker}
                  maxZoom={maxZoom}
                  shouldPadPopup={shouldPadPopup}
                >
                  {props.markerPopup}
                </MarkerPin>
              ))}

              {clusters.map((cluster) => (
                <ClusterPin<P>
                  key={cluster.properties.cluster_id}
                  cluster={cluster}
                  sc={sc}
                  maxZoom={maxZoom}
                >
                  {props.clusterPopup}
                </ClusterPin>
              ))}

              {props.children?.(map, initialBounds, markers, clusters)}
            </>
          )}
        </MapController>
      </LeafletMap>
    </div>
  );
}
